import React, { useContext } from 'react'

import {
  AppBar,
  AppTheme,
  Drawer,
  IconButton,
  makeAppStyles,
  RoundedPlainTextButton,
  useMediaQuery,
} from '@percept/mui'

import { Close, Menu } from '@percept/mui/icons'

import { HomeButton } from 'components/HomeButton'

import { DrawerContent, DrawerContentProps } from './DrawerContent'

import { UserFavouritesPopover, UserPopover } from '@percept/app-components'

import { useActiveOrgEnvironment } from 'components/Organisation'

import { OrgUnitTreePopover } from 'components/OrgUnitTree'

import { UserDetailContext } from '@percept/app-components'

import { useLocationParams } from '@percept/hooks'

import { useAppDrawerState } from 'hooks'

import { Breakpoint } from '@material-ui/core/styles/createBreakpoints'
import { OrgUnitReportingAvailabilityProvider } from 'screens/dataProviders'


const drawerWidths: Record<Breakpoint, number> = {
  xs: 260,
  sm: 260,
  md: 196,
  lg: 196,
  xl: 240,
}

export const useAppBarStyles = makeAppStyles( theme => ({
  appBarRoot: {
    flexDirection: 'row',
  },
  appBarOffset: theme.mixins.toolbar,
  toolbarContentLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  toolbarContentRight: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  toolbarContentSpacedLeft: {
    marginLeft: theme.spacing(1.5),
  },
  toolbarContentSpacedRight: {
    marginRight: theme.spacing(1.5),
  },
  appBar: {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.only('xl')]: {
      width: `calc(100% - ${drawerWidths.xl}px)`,
      marginLeft: drawerWidths.xl,
    },
    [theme.breakpoints.only('lg')]: {
      width: `calc(100% - ${drawerWidths.lg}px)`,
      marginLeft: drawerWidths.lg,
    },
    [theme.breakpoints.only('md')]: {
      width: `calc(100% - ${drawerWidths.md}px)`,
      marginLeft: drawerWidths.md,
    },
  },
  homeButton: {
    color: theme.palette.primary.main,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
}))

const useStyles = makeAppStyles( theme => ({
  root: {
    // display: 'flex',
  },
  hide: {
    display: 'none',
  },
  drawer: {
    flexShrink: 0,
    [theme.breakpoints.only('xl')]: {
      width: drawerWidths.xl,
    },
    [theme.breakpoints.only('lg')]: {
      width: drawerWidths.lg,
    },
    [theme.breakpoints.only('md')]: {
      width: drawerWidths.md,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  temporaryDrawer: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  drawerPaper: {
    [theme.breakpoints.only('xl')]: {
      width: drawerWidths.xl,
    },
    [theme.breakpoints.only('lg')]: {
      width: drawerWidths.lg,
    },
    [theme.breakpoints.only('md')]: {
      width: drawerWidths.md,
    },
    [theme.breakpoints.only('sm')]: {
      width: drawerWidths.sm,
    },
    [theme.breakpoints.only('xs')]: {
      width: drawerWidths.xs,
    },
  },
  drawerCloseButton: {
    margin: theme.spacing(1.5, 0, 0, 1.5),
    maxWidth: 'min-content',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  },
  content: {
    flexGrow: 1,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    // [theme.breakpoints.only('xl')]: {
    //   marginLeft: -drawerWidths.xl,
    // },
    // [theme.breakpoints.only('lg')]: {
    //   marginLeft: -drawerWidths.lg,
    // },
    // [theme.breakpoints.only('md')]: {
    //   marginLeft: -drawerWidths.md,
    // },
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    [theme.breakpoints.only('xl')]: {
      marginLeft: drawerWidths.xl,
    },
    [theme.breakpoints.only('lg')]: {
      marginLeft: drawerWidths.lg,
    },
    [theme.breakpoints.only('md')]: {
      marginLeft: drawerWidths.md,
    },
  },
}))


export const SidebarLayout = ({ children }: React.PropsWithChildren<{}>): JSX.Element => {
  const {
    activePlatformUnit,
  } = useActiveOrgEnvironment()

  const user = useContext(UserDetailContext)

  const isMobileDrawer = useMediaQuery((theme: AppTheme) => theme.breakpoints.down('sm'))

  const [drawerState, setDrawerState] = useAppDrawerState()

  // Default drawer state on first load to open for non-mobile views only
  const drawerOpen = (
    drawerState === null ?
      !isMobileDrawer :
      drawerState
  )

  const toggleDrawerState = (): void => {
    setDrawerState(!drawerOpen)
  }

  const params = useLocationParams()

  const onNavigate: DrawerContentProps['onNavigate'] = (linkItem) => {
    if( isMobileDrawer && linkItem.route ){
      setDrawerState(false)
    }
  }

  const drawerContent = <DrawerContent onNavigate={onNavigate} />

  const classes = useStyles()

  const appBarClasses = useAppBarStyles()

  let appBarClassName = appBarClasses.appBar
  let mainContentClassName = classes.content
  if( drawerOpen ){
    mainContentClassName += (' ' + classes.contentShift)
    appBarClassName += (' ' + appBarClasses.appBarShift)
  }

  return (
    <div className={classes.root}>
      <AppBar
        position='fixed'
        color='inherit'
        elevation={0}
        className={appBarClassName}
        classes={{
          root: appBarClasses.appBarRoot,
        }}>
        <div className={appBarClasses.toolbarContentLeft}>
          <IconButton
            onClick={toggleDrawerState}>
            <Menu />
          </IconButton>
          <HomeButton
            className={appBarClasses.homeButton}
            opacity={1} />
          { activePlatformUnit && (
            <div className={appBarClasses.toolbarContentSpacedLeft}>
              <OrgUnitTreePopover
                ButtonComponent={RoundedPlainTextButton}
                buttonContent={activePlatformUnit.name}
                activeId={params.series_id || activePlatformUnit.id}
                activeItemType={params.series_id ? 'REPORT_SERIES' : 'PLATFORM_UNIT'}
                orgUnits={user.org_units} />
            </div>
          )}
        </div>

        <div className={appBarClasses.toolbarContentRight}>
          {/* <PlatformAdminMenu /> */}
          <UserFavouritesPopover
            className={appBarClasses.toolbarContentSpacedRight} />
          <UserPopover />
        </div>
      </AppBar>

      <OrgUnitReportingAvailabilityProvider>

        { isMobileDrawer && (
          <Drawer
            className={classes.temporaryDrawer}
            variant='temporary'
            open={drawerOpen}
            onClose={toggleDrawerState}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}>
            <IconButton
              className={classes.drawerCloseButton}
              onClick={toggleDrawerState}>
              <Close />
            </IconButton>
            { drawerContent }
          </Drawer>
        )}

        { !isMobileDrawer && (
          <Drawer
            className={classes.drawer}
            variant='persistent'
            anchor='left'
            open={drawerOpen}
            classes={{
              paper: classes.drawerPaper,
            }}>
            { drawerContent }
          </Drawer>
        )}

        <div className={mainContentClassName}>
          <div className={appBarClasses.appBarOffset} />
          {children}
        </div>

      </OrgUnitReportingAvailabilityProvider>

    </div>
  )
}
